import { z } from "zod";
import { assigneeTypeSchema } from "./assignments";

const text = (max: number) => z.string().trim().min(1).max(max);
const optionalId = z.string().uuid().nullable().optional();

export const obligationTypeSchema = z.enum([
  "customer_promise", "contract_renewal", "saas_renewal", "certificate_expiration",
  "compliance_remediation", "security_remediation", "implementation_milestone",
  "access_review", "other",
]);
export const signalSeveritySchema = z.enum(["info", "low", "medium", "high", "critical"]);
export const signalStatusSchema = z.enum(["open", "blocked", "resolved", "dismissed"]);
export const obligationStateSchema = z.enum([
  "detected", "needs_review", "active", "approaching", "overdue", "blocked",
  "completed", "dismissed", "unverifiable",
]);

export const normalizedSignalInputSchema = z.object({
  provider: text(80),
  connectionId: optionalId,
  externalId: text(300),
  externalUrl: z.string().url().max(2_000).nullable().optional(),
  type: obligationTypeSchema,
  severity: signalSeveritySchema,
  status: signalStatusSchema.default("open"),
  title: text(300),
  summary: text(2_000),
  evidenceExcerpt: text(2_000),
  observedAt: z.string().datetime(),
  sourceUpdatedAt: z.string().datetime(),
  deadlineAt: z.string().datetime().nullable().optional(),
  affectedResources: z.array(text(200)).max(25).default([]),
  candidateImpact: text(1_000),
  customerId: optionalId,
  ownerType: text(40).nullable().optional(),
  ownerId: text(200).nullable().optional(),
  confidence: z.number().min(0).max(1),
  provenance: z.enum(["deterministic", "model", "reviewed"]),
});

export const externalWorkObligationCandidateSchema = z.object({
  externalId: text(300),
  title: text(300),
  summary: text(2_000),
  evidenceExcerpt: text(2_000),
  sourceUpdatedAt: z.string().datetime(),
  externalUrl: z.string().url().max(2_000).nullable(),
  labels: z.array(text(200)).max(50),
  affectedResources: z.array(text(200)).max(25),
  ownerType: text(40).nullable(),
  ownerId: text(200).nullable(),
  obligationType: obligationTypeSchema.nullable(),
  customerImpact: text(1_000).nullable(),
  deadlineAt: z.string().datetime().nullable(),
  sourceStatus: signalStatusSchema,
  eligible: z.boolean(),
  invalidReason: z.enum(["missing_type", "invalid_type", "missing_impact", "invalid_deadline", "conflicting_field"]).nullable(),
});

export const obligationBridgeDispositionSchema = z.enum([
  "created", "updated", "unchanged", "skipped_ineligible", "skipped_invalid",
  "retryable_failure", "terminal_failure",
]);
export const obligationBridgeResultSchema = z.object({
  disposition: obligationBridgeDispositionSchema,
  signalIdentity: text(700),
  obligationId: z.string().uuid().nullable(),
  reasonCode: z.enum([
    "eligible", "label_missing", "missing_type", "invalid_type", "missing_impact",
    "invalid_deadline", "conflicting_field", "transient_failure", "terminal_failure",
  ]),
});

export const obligationSourceSchema = z.object({
  signalId: z.string().uuid(), provider: text(80), externalId: text(300),
  externalUrl: z.string().url().nullable(), evidenceExcerpt: text(2_000),
  sourceUpdatedAt: z.string().datetime(),
});

export const operationalObligationSchema = z.object({
  id: z.string().uuid(), type: obligationTypeSchema, state: obligationStateSchema,
  title: text(300), summary: text(2_000), impact: text(1_000), severity: signalSeveritySchema,
  confidence: z.number().min(0).max(1), provenance: z.enum(["deterministic", "model", "reviewed"]),
  requiresReview: z.boolean(), deadlineAt: z.string().datetime().nullable(),
  customerId: z.string().uuid().nullable(), ownerType: z.string().nullable(), ownerId: z.string().nullable(),
  ownershipOrigin: z.enum(["source", "reviewed", "unassigned", "unknown"]).optional(),
  verification: z.enum(["pending", "source_verified", "manually_confirmed", "unverifiable"]),
  freshness: z.enum(["current", "stale"]), source: obligationSourceSchema,
  detectedAt: z.string().datetime(), updatedAt: z.string().datetime(),
});

export const obligationAttentionSummarySchema = z.object({
  total: z.number().int().nonnegative(), needsReview: z.number().int().nonnegative(),
  approaching: z.number().int().nonnegative(), overdue: z.number().int().nonnegative(),
  blocked: z.number().int().nonnegative(), completed: z.number().int().nonnegative(),
});
export const obligationListResponseSchema = z.object({
  obligations: z.array(operationalObligationSchema), summary: obligationAttentionSummarySchema,
});
export const upsertSignalResponseSchema = z.object({ obligation: operationalObligationSchema, created: z.boolean() });
export const obligationActionSchema = z.discriminatedUnion("action", [
  z.object({
    action: z.literal("confirm"),
    ownerType: assigneeTypeSchema,
    ownerId: text(200),
  }),
  z.object({
    action: z.literal("assign"),
    ownerType: assigneeTypeSchema,
    ownerId: text(200),
  }),
  z.object({
    action: z.literal("dismiss"),
    reason: z.string().trim().min(10).max(500),
  }),
]);
export const obligationActionResponseSchema = z.object({ obligation: operationalObligationSchema });

export type NormalizedSignalInput = z.infer<typeof normalizedSignalInputSchema>;
export type ExternalWorkObligationCandidate = z.infer<typeof externalWorkObligationCandidateSchema>;
export type ObligationBridgeResult = z.infer<typeof obligationBridgeResultSchema>;
export type OperationalObligation = z.infer<typeof operationalObligationSchema>;
export type ObligationListResponse = z.infer<typeof obligationListResponseSchema>;
export type ObligationAction = z.infer<typeof obligationActionSchema>;
